import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Background } from "../components/Background";
import { MusicToggle } from "../components/MusicToggle";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { ShieldLogo } from "../components/ui/Logo";
import { useSocket } from "../state/SocketContext";

type Mode = "menu" | "create" | "join";

export default function Home() {
  const navigate = useNavigate();
  const { createRoom, joinRoom, connState } = useSocket();
  const [mode, setMode] = useState<Mode>("menu");
  const [name, setName] = useState(() => localStorage.getItem("cap.name") ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const persistName = (n: string) => {
    localStorage.setItem("cap.name", n);
  };

  const goSinglePlayer = () => {
    persistName(name || "Cap");
    navigate("/play/single");
  };

  const submitCreate = async () => {
    setError(null);
    setBusy(true);
    persistName(name);
    const res = await createRoom(name || "Cap");
    setBusy(false);
    if (!res.ok) {
      setError(humanError(res.error));
      return;
    }
    navigate("/lobby");
  };

  const submitJoin = async () => {
    setError(null);
    if (!code.trim()) {
      setError("Enter a room code.");
      return;
    }
    setBusy(true);
    persistName(name);
    const res = await joinRoom(code.trim(), name || "Cap");
    setBusy(false);
    if (!res.ok) {
      setError(humanError(res.error));
      return;
    }
    navigate("/lobby");
  };

  return (
    <Background>
      <div className="absolute right-4 top-4 z-20">
        <MusicToggle />
      </div>
      <div className="mx-auto flex min-h-screen max-w-5xl flex-col items-center justify-center px-4 py-10">
        <header className="flex flex-col items-center gap-4 text-center">
          <div className="animate-float">
            <ShieldLogo size={120} className="drop-shadow-[0_0_30px_rgba(200,16,46,0.45)]" />
          </div>
          <h1 className="font-display text-5xl uppercase leading-none tracking-[0.18em] sm:text-7xl">
            <span className="text-cap-red">Captain</span>{" "}
            <span className="text-white">America</span>
          </h1>
          <p className="max-w-xl text-sm uppercase tracking-[0.3em] text-white/60 sm:text-base">
            A real-time multiplayer endless runner
          </p>
        </header>

        <main className="mt-10 w-full max-w-md">
          <Card glow>
            <ConnectionPill state={connState} />

            {mode === "menu" && (
              <div className="mt-2 flex flex-col gap-3">
                <NameInput value={name} onChange={setName} />
                <Button variant="primary" size="lg" block onClick={() => setMode("create")}>
                  Create Room
                </Button>
                <Button variant="secondary" size="lg" block onClick={() => setMode("join")}>
                  Join Room
                </Button>
                <Button variant="gold" size="lg" block onClick={goSinglePlayer}>
                  Single Player
                </Button>
              </div>
            )}

            {mode === "create" && (
              <FormShell onBack={() => { setMode("menu"); setError(null); }}>
                <NameInput value={name} onChange={setName} />
                <Button
                  variant="primary"
                  size="lg"
                  block
                  disabled={busy || connState !== "connected"}
                  onClick={submitCreate}
                >
                  {busy ? "Assembling…" : "Create"}
                </Button>
              </FormShell>
            )}

            {mode === "join" && (
              <FormShell onBack={() => { setMode("menu"); setError(null); }}>
                <NameInput value={name} onChange={setName} />
                <label className="text-xs uppercase tracking-widest text-white/60">
                  Room Code
                </label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 8))}
                  placeholder="ABCDE"
                  className="rounded-full border-2 border-white/20 bg-white/5 px-4 py-2.5 text-center font-display text-xl tracking-[0.4em] uppercase text-white placeholder:text-white/30 focus:border-cap-gold focus:outline-none"
                />
                <Button
                  variant="secondary"
                  size="lg"
                  block
                  disabled={busy || connState !== "connected"}
                  onClick={submitJoin}
                >
                  {busy ? "Joining…" : "Join"}
                </Button>
              </FormShell>
            )}

            {error && (
              <p className="mt-3 rounded-lg border border-cap-red/40 bg-cap-red/10 px-3 py-2 text-center text-sm text-cap-red">
                {error}
              </p>
            )}
          </Card>

          <div className="mt-6 text-center text-xs uppercase tracking-widest text-white/40">
            Controls — Space/↑ Jump • ↓ Duck • F Shield Throw • P Pause
          </div>
        </main>
      </div>
    </Background>
  );
}

function NameInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs uppercase tracking-widest text-white/60">Hero Name</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\p{L}\p{N}_\- ]/gu, "").slice(0, 20))}
        placeholder="Captain"
        className="rounded-full border-2 border-white/20 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-cap-gold focus:outline-none"
      />
    </div>
  );
}

function FormShell({ children, onBack }: { children: React.ReactNode; onBack: () => void }) {
  return (
    <div className="mt-2 flex flex-col gap-3">
      {children}
      <button
        className="mx-auto text-xs uppercase tracking-widest text-white/50 hover:text-white"
        onClick={onBack}
      >
        ← Back
      </button>
    </div>
  );
}

function ConnectionPill({ state }: { state: "idle" | "connecting" | "connected" | "disconnected" }) {
  const map = {
    idle: { label: "Idle", c: "bg-white/30" },
    connecting: { label: "Connecting", c: "bg-yellow-400" },
    connected: { label: "Connected", c: "bg-emerald-400" },
    disconnected: { label: "Offline", c: "bg-cap-red" },
  } as const;
  const { label, c } = map[state];
  return (
    <div className="mb-3 flex items-center justify-center gap-2 text-[10px] uppercase tracking-widest text-white/60">
      <span className={`inline-block h-2 w-2 rounded-full ${c}`} />
      Server {label}
    </div>
  );
}

function humanError(code?: string): string {
  switch (code) {
    case "ROOM_FULL":
      return "Room is full.";
    case "MATCH_IN_PROGRESS":
      return "Match already in progress.";
    case "INVALID_CODE":
      return "Invalid room code.";
    case "ROOM_NOT_FOUND":
      return "Room not found.";
    case "INTERNAL_ERROR":
      return "Server unreachable.";
    default:
      return "Something went wrong.";
  }
}
